import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../../stores/workbench';
import { normalizeWorkbenchPath } from '../../utils/normalizeWorkbenchPath';
import { platformFileRefusal } from './platformFiles';

const CONFIG_PATHS = new Set([
  'package.json',
  'vite.config.ts',
  'tsconfig.json',
  'index.html',
  'tailwind.config.js',
  'postcss.config.js',
]);

/** Paths the model must never delete (template infra + config). */
export function isDeleteProtectedPath(filePath: string): boolean {
  const normalized = normalizeWorkbenchPath(filePath);
  if (!normalized) return true;
  if (normalized.startsWith('src/components/ui/')) return true;
  if (normalized === 'src/lib/utils.ts') return true;
  if (normalized === 'src/index.css') return true;
  if (normalized === 'src/main.tsx') return true;
  if (normalized === 'src/App.tsx') return true;
  if (CONFIG_PATHS.has(normalized)) return true;
  return false;
}

export async function executeDeleteFile(
  webcontainer: WebContainer | null,
  filePath: string,
  signal?: AbortSignal,
): Promise<string> {
  const resolvedPath = normalizeWorkbenchPath(filePath);
  if (!resolvedPath) {
    return 'Error: invalid path';
  }

  if (isDeleteProtectedPath(resolvedPath)) {
    return `Error: ${resolvedPath} is protected and cannot be deleted`;
  }

  const refusal = platformFileRefusal(resolvedPath, workbenchStore.files.get());
  if (refusal) return refusal;

  const manualEntry = workbenchStore.files.get()[resolvedPath];
  if (manualEntry?.modified) {
    return `Error: ${resolvedPath} has manual edits — delete rejected`;
  }

  if (signal?.aborted) {
    return `Skipped: turn was cancelled before ${resolvedPath} could be deleted.`;
  }

  if (webcontainer) {
    try {
      await webcontainer.fs.rm(resolvedPath);
    } catch {
      return `Error: file not found: ${resolvedPath}`;
    }
  } else if (workbenchStore.files.get()[resolvedPath] === undefined) {
    // The light preview has no filesystem to consult: the store is the workspace, and a path
    // it does not hold is a path the preview never had.
    return `Error: file not found: ${resolvedPath}`;
  }

  workbenchStore.removeFile(resolvedPath);
  return `Deleted ${resolvedPath}`;
}
